import {
  CategoryIcon,
  DeleteIcon,
  PublishArrowDownIcon,
  PublishArrowUpIcon,
} from '@/assets/svg'
import { Pagination } from '@/components/fragments'
import { CustomSwitch } from '@/components/fragments/switch'
import WrapperCard from '@/components/fragments/wrapper-card'
import { DeleteModal } from '@/components/modals'
import ConfirmationModal from '@/components/modals/confirmation-modal'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  useBulkUpdateBusinessTypesMutation,
  useCreateBusinessTypeMutation,
  useDeleteBusinessTypesMutation,
  useGetBusinessTypeListQuery,
  usePublishAllBusinessTypesMutation,
  useUnpublishAllBusinessTypesMutation,
  useBulkDeleteBusinessTypesMutation,
  useUpdateBusinessTypeMutation,
} from '@/redux/services/settings/business-types'
import { MenuCategoriesSkeleton } from '@/skeletons'
import { useDebounce } from '@/utils/hooks/useDebounce'
import { Edit, Plus, X } from 'lucide-react'
import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import AddBusinessTypeModal from './modals/add-business-type-modal'

interface BusinessType {
  id: number
  name: string
  description?: string
  image_url?: string
  is_published: boolean
  vendors_count?: number
}

const BusinessTypes = () => {
  const [hasChanges, setHasChanges] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingBusinessType, setEditingBusinessType] =
    useState<BusinessType | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [businessTypeToDelete, setBusinessTypeToDelete] =
    useState<BusinessType | null>(null)
  const [editingBusinessTypes, setEditingBusinessTypes] = useState<
    Record<string, BusinessType>
  >({})
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(15)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedBusinessTypes, setSelectedBusinessTypes] = useState<number[]>(
    []
  )
  const [showBulkPublishModal, setShowBulkPublishModal] = useState(false)
  const [showBulkUnpublishModal, setShowBulkUnpublishModal] = useState(false)
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false)
  const [modifiedBusinessTypes, setModifiedBusinessTypes] = useState<
    Record<
      string,
      { name: string; is_published: boolean; image?: File | string }
    >
  >({})
  const [currentDataChanged, setCurrentDataChanged] = useState(false)
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  const { data: businessTypesData, isLoading } = useGetBusinessTypeListQuery(
    {
      page: currentPage,
      per_page: pageSize,
      search: debouncedSearchQuery,
    },
    {
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    }
  )

  const [addBusinessType, { isLoading: isAdding }] =
    useCreateBusinessTypeMutation()
  const [updateBusinessType, { isLoading: isUpdating }] =
    useUpdateBusinessTypeMutation()
  const [deleteBusinessTypes, { isLoading: isDeletingAll }] =
    useDeleteBusinessTypesMutation()
  const [bulkDeleteBusinessTypes, { isLoading: isBulkDeleting }] =
    useBulkDeleteBusinessTypesMutation()
  const [publishAllBusinessTypes, { isLoading: isPublishing }] =
    usePublishAllBusinessTypesMutation()
  const [unpublishAllBusinessTypes, { isLoading: isUnpublishing }] =
    useUnpublishAllBusinessTypesMutation()
  const [bulkUpdateBusinessTypes, { isLoading: isBulkUpdating }] =
    useBulkUpdateBusinessTypesMutation()

  // Debounced search handler
  const handleSearch = (value: string) => {
    setDebouncedSearchQuery(value)
    setCurrentPage(1)
  }

  const debouncedSearch = useDebounce(handleSearch, 500)

  // Auto-select items when navigating to a new page
  useEffect(() => {
    if (businessTypesData?.business_types && selectedBusinessTypes.length > 0) {
      const currentPageIds = businessTypesData.business_types.map(
        (bt: BusinessType) => bt.id
      )
      const hasNewItems = currentPageIds.some(
        (id: number) => !selectedBusinessTypes.includes(id)
      )

      if (hasNewItems) {
        setSelectedBusinessTypes((prev) => {
          const newSelection = [...prev]
          currentPageIds.forEach((id: number) => {
            if (!newSelection.includes(id)) {
              newSelection.push(id)
            }
          })
          return newSelection
        })
      }
    }
  }, [currentPage, businessTypesData?.business_types])

  const handleInputChange = (value: string) => {
    setSearchQuery(value)
    debouncedSearch(value)
  }

  const handleTogglePublish = (id: number) => {
    if (editingBusinessTypes[id]) {
      // Update the editing business type
      setEditingBusinessTypes((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          is_published: !prev[id].is_published,
        },
      }))
      setHasChanges(true)
    } else {
      // Track modification for bulk update
      const businessType = businessTypesData?.business_types?.find(
        (bt: BusinessType) => bt.id === id
      )
      if (businessType) {
        setModifiedBusinessTypes((prev) => ({
          ...prev,
          [id]: {
            name: businessType.name,
            is_published: !businessType.is_published,
          },
        }))
        setHasChanges(true)
      }
    }
  }

  const handleDeleteBusinessType = (businessType: BusinessType) => {
    setBusinessTypeToDelete(businessType)

    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    if (!businessTypeToDelete) return

    try {
      await deleteBusinessTypes(businessTypeToDelete.id)
        .unwrap()
        .then((res: any) => {
          if (res.success) {
            toast.success(res.message || res.data?.message)
            setShowDeleteModal(false)
            setBusinessTypeToDelete(null)

            // Reset to first page if we're on a page that no longer exists
            if (currentPage > 1 && businessTypesData?.pagination) {
              const remainingItems = businessTypesData.pagination.total - 1
              const newLastPage = Math.ceil(remainingItems / pageSize)
              if (currentPage > newLastPage) {
                setCurrentPage(newLastPage)
              }
            }
          } else {
            toast.error(res.message || res.data?.message)
          }
        })
        .catch((err: any) => {
          toast.error(err.data?.message || err.message)
        })
    } catch (error) {
      console.error('Error deleting business type:', error)
      toast.error('Failed to delete business type')
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setBusinessTypeToDelete(null)
  }

  const handleAddBusinessType = () => {
    setShowAddModal(true)
  }

  const handleEditBusinessType = (businessType: BusinessType) => {
    // For inline editing (name only)
    setEditingBusinessTypes((prev) => ({
      ...prev,
      [businessType.id]: businessType,
    }))
  }

  const handleEditBusinessTypeModal = (businessType: BusinessType) => {
    // For modal editing (with image support)
    setEditingBusinessType(businessType)
    setShowEditModal(true)
  }

  const handleUpdateBusinessTypeSubmit = async (data: {
    name: string
    image?: File
    is_published?: boolean
  }) => {
    if (!editingBusinessType) return

    const isPublished = data.is_published ?? editingBusinessType.is_published

    // Always use FormData when image is provided
    const formData = new FormData()
    formData.append('id', editingBusinessType.id.toString())
    formData.append('name', data.name)
    formData.append('is_published', isPublished ? '1' : '0')
    formData.append('_method', 'PUT')
    if (data.image) {
      formData.append('image', data.image)
    }

    try {
      await updateBusinessType(formData)
        .unwrap()
        .then((res: any) => {
          if (res.success) {
            toast.success(res.message || res.data?.message)
            setShowEditModal(false)
            setEditingBusinessType(null)
          } else {
            toast.error(res.message || res.data?.message)
          }
        })
        .catch((err: any) => {
          toast.error(err.data?.message || err.message)
        })
    } catch (error) {
      console.error('Error updating business type:', error)
      toast.error('Failed to update business type')
    }
  }

  const handleResetEdit = (businessTypeId?: number) => {
    if (businessTypeId) {
      setEditingBusinessTypes((prev) => {
        const newState = { ...prev }
        delete newState[businessTypeId]
        return newState
      })
    } else {
      setEditingBusinessTypes({})
    }
  }

  const handleToggleBusinessTypeSelection = (businessTypeId: number) => {
    setSelectedBusinessTypes((prev: number[]) =>
      prev.includes(businessTypeId)
        ? prev.filter((id: number) => id !== businessTypeId)
        : [...prev, businessTypeId]
    )
  }

  const handleSelectAll = () => {
    const allBusinessTypeIds =
      businessTypesData?.business_types?.map((bt: BusinessType) => bt.id) || []
    setSelectedBusinessTypes((prev: number[]) => {
      const newSelection: number[] = [...prev]
      allBusinessTypeIds.forEach((id: number) => {
        if (!newSelection.includes(id)) {
          newSelection.push(id)
        }
      })
      return newSelection
    })
  }

  const handlePublishAll = () => {
    if (selectedBusinessTypes.length === 0) {
      toast.error('Please select business types to publish')
      return
    }
    setShowBulkPublishModal(true)
  }

  const handleUnpublishAll = () => {
    if (selectedBusinessTypes.length === 0) {
      toast.error('Please select business types to unpublish')
      return
    }
    setShowBulkUnpublishModal(true)
  }

  const handleDeleteAll = () => {
    if (selectedBusinessTypes.length === 0) {
      toast.error('Please select business types to delete')
      return
    }
    setShowBulkDeleteModal(true)
  }

  const handleConfirmBulkPublish = async () => {
    const payload = {
      ids: selectedBusinessTypes,
    }

    await publishAllBusinessTypes(payload)
      .unwrap()
      .then((res: any) => {
        if (res.success) {
          toast.success(res.message || res.data?.message)
          setSelectedBusinessTypes([])
          setShowBulkPublishModal(false)
          setCurrentDataChanged(true)
        } else {
          toast.error(res.message || res.data?.message)
        }
      })
      .catch((err: any) => {
        toast.error(err.data.message || err.message)
      })
      .finally(() => {
        setCurrentDataChanged(false)
      })
  }

  const handleConfirmBulkUnpublish = async () => {
    const payload = {
      ids: selectedBusinessTypes,
    }

    await unpublishAllBusinessTypes(payload)
      .unwrap()
      .then((res: any) => {
        if (res.success) {
          toast.success(res.message || res.data?.message)
          setSelectedBusinessTypes([])
          setShowBulkUnpublishModal(false)
        } else {
          toast.error(res.message || res.data?.message)
        }
      })
      .catch((err: any) => {
        toast.error(err.data.message || err.message)
      })
  }

  const handleConfirmBulkDelete = async () => {
    const payload = {
      ids: selectedBusinessTypes,
    }

    await bulkDeleteBusinessTypes(payload)
      .unwrap()
      .then((res: any) => {
        if (res.success) {
          toast.success(res.message || res.data?.message)
          setSelectedBusinessTypes([])
          setShowBulkDeleteModal(false)
        } else {
          toast.error(res.message || res.data?.message)
        }
      })
      .catch((err: any) => {
        toast.error(err.data?.message || err.message)
      })
  }

  const handleAddBusinessTypeSubmit = async (data: {
    name: string
    image?: File
    is_published?: boolean
  }) => {
    const isPublished = data.is_published ?? false
    let payload: any = { ...data, is_published: isPublished }

    // If image is provided, create FormData
    if (data.image) {
      const formData = new FormData()
      formData.append('name', data.name)
      formData.append('is_published', isPublished ? '1' : '0')
      formData.append('image', data.image)
      payload = formData
    }

    await addBusinessType(payload)
      .unwrap()
      .then((res: any) => {
        if (res.success) {
          toast.success(res.message || res.data?.message)
          setCurrentPage(1)
          setShowAddModal(false)
        } else {
          toast.error(res.message || res.data?.message)
        }
      })
      .catch((err: any) => {
        toast.error(err.data.message || err.message)
      })
  }

  const handleSaveChanges = async () => {
    // Combine modified business types and editing business types
    const allChanges = { ...modifiedBusinessTypes }

    // Add editing business types to changes (inline editing only handles name and is_published)
    Object.entries(editingBusinessTypes).forEach(([id, businessType]) => {
      allChanges[id] = {
        name: businessType.name,
        is_published: businessType.is_published,
      }
    })

    if (Object.keys(allChanges).length === 0) {
      toast.error('No changes to save')
      return
    }

    // Inline editing only supports name and is_published changes
    // Image updates should be done through the modal
    const payload = {
      business_types: Object.entries(allChanges).map(([id, data]) => ({
        id: parseInt(id),
        ...(data.name && { name: data.name }),
        is_published: data.is_published,
      })),
    }

    try {
      await bulkUpdateBusinessTypes(payload)
        .unwrap()
        .then((res: any) => {
          if (res.success) {
            toast.success(res.message || res.data?.message)
            setModifiedBusinessTypes({})
            setEditingBusinessTypes({})
            setHasChanges(false)
          } else {
            toast.error(res.message || res.data?.message)
          }
        })
        .catch((err: any) => {
          toast.error(err.data?.message || err.message)
        })
    } catch (error) {
      console.error('Error saving changes:', error)
      toast.error('Failed to save changes')
    }
  }

  const handleReset = () => {
    // Clear selected business types and reset checkboxes to original state
    setSelectedBusinessTypes([])
    setModifiedBusinessTypes({})
    setEditingBusinessTypes({})
    setHasChanges(false)
  }

  return (
    <div className='space-y-6 mt-4'>
      <WrapperCard
        title='Business Types'
        icon={CategoryIcon}
        wrapperClassName='h-fit relative'
        headerClassName='flex flex-row-reverse justify-end items-center gap-2 text-[#424242]'
      >
        <div className='space-y-6'>
          <div className='flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 sm:gap-x-3 mt-4 sm:mt-0 sm:absolute sm:top-5 sm:right-5'>
            <div className='w-full sm:w-64'>
              <Input
                placeholder='Search types by name or descriptions...'
                className='w-full rounded-[8px]'
                id='search'
                name='search'
                value={searchQuery}
                onChange={(e) => handleInputChange(e.target.value)}
              />
            </div>
            <div className='w-full sm:w-auto'>
              <Button
                onClick={handleAddBusinessType}
                className='bg-primary hover:bg-primary/90 text-primary-foreground p-2 sm:p-3 sm:px-4 sm:py-2 rounded-[8px] flex items-center justify-center space-x-2 w-full sm:w-auto'
              >
                <Plus className='h-4 w-4' />
                <span className='hidden sm:block'>Add Business Type</span>
                <span className='sm:hidden'>Add</span>
              </Button>
            </div>
          </div>

          <div className='h-0 sm:h-0'></div>

          <div className='flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-0 mb-4'>
            <div className='flex  sm:flex-row items-center gap-2 sm:gap-x-3'>
              <Button
                onClick={handleUnpublishAll}
                variant={'ghost'}
                size={'default'}
                className='w-fit rounded-[8px] flex-1 sm:flex-initial'
                disabled={selectedBusinessTypes.length === 0 || isUnpublishing}
              >
                {PublishArrowDownIcon}
                <span className='hidden sm:inline ml-2'>Unpublish All</span>
              </Button>
              <Button
                onClick={handlePublishAll}
                variant={'ghost'}
                size={'default'}
                className='w-fit rounded-[8px] flex-1 sm:flex-initial'
                disabled={selectedBusinessTypes.length === 0 || isPublishing}
              >
                <PublishArrowUpIcon className='h-5 w-5 text-black sm:mr-1' />
                <span className='hidden sm:inline ml-2'>Publish All</span>
              </Button>
              <Button
                onClick={handleDeleteAll}
                variant={'destructive'}
                size={'default'}
                className='w-fit rounded-[8px] text-white flex-1 sm:flex-initial'
                disabled={selectedBusinessTypes.length === 0 || isBulkDeleting}
                loading={isBulkDeleting}
              >
                <DeleteIcon className='h-5 w-5 text-white sm:mr-1' />
                <span className='hidden sm:inline'>Delete Selected</span>
              </Button>
            </div>
          </div>
          <div className='flex items-center gap-2 flex-wrap'>
            <Checkbox
              checked={
                businessTypesData?.business_types?.every((bt: BusinessType) =>
                  selectedBusinessTypes.includes(bt.id)
                ) && businessTypesData?.business_types?.length > 0
              }
              onChange={() => {
                const allCurrentPageSelected =
                  businessTypesData?.business_types?.every((bt: BusinessType) =>
                    selectedBusinessTypes.includes(bt.id)
                  )

                if (allCurrentPageSelected) {
                  // Deselect ALL items (not just current page)
                  setSelectedBusinessTypes([])
                } else {
                  handleSelectAll()
                }
              }}
              id='select-all'
            />
            <label
              htmlFor='select-all'
              className='text-xs sm:text-sm font-medium text-gray-700 -mt-2'
            >
              <span className='hidden sm:inline'>Select All </span>
              <span>({selectedBusinessTypes.length} business types)</span>
            </label>
          </div>
          <div className='space-y-3 mt-6 bg-[#FAFAFA] rounded-[12px] p-3 sm:p-4'>
            {isLoading ? (
              <MenuCategoriesSkeleton />
            ) : (
              <>
                {businessTypesData?.business_types?.map(
                  (businessType: BusinessType) => {
                    return (
                      <div
                        key={businessType.id}
                        className='rounded-[12px] p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 bg-white'
                      >
                        <div className='flex flex-col gap-y-2 w-full'>
                          <div className='flex items-start gap-3'>
                            <div>
                              <Checkbox
                                checked={selectedBusinessTypes.includes(
                                  businessType.id
                                )}
                                id={businessType.id}
                                name={''}
                                onChange={() =>
                                  handleToggleBusinessTypeSelection(
                                    businessType.id
                                  )
                                }
                              />
                            </div>
                            {businessType.image_url && (
                              <div className='flex-shrink-0'>
                                <img
                                  src={businessType.image_url}
                                  alt={businessType.name}
                                  className='w-16 h-16 rounded-lg object-cover border border-gray-200'
                                />
                              </div>
                            )}
                            <div className='flex-1 min-w-0'>
                              {editingBusinessTypes[businessType.id] ? (
                                <div className='flex justify-between items-center gap-2 w-full'>
                                  <div className='w-full'>
                                    <Input
                                      id='name'
                                      name='name'
                                      value={
                                        editingBusinessTypes[businessType.id]
                                          ?.name
                                      }
                                      onChange={(e) => {
                                        const originalBusinessType =
                                          businessTypesData?.business_types?.find(
                                            (bt: BusinessType) =>
                                              bt.id === businessType.id
                                          )
                                        const hasChanged =
                                          e.target.value !==
                                          originalBusinessType?.name

                                        setEditingBusinessTypes((prev) => ({
                                          ...prev,
                                          [businessType.id]: {
                                            ...prev[businessType.id],
                                            name: e.target.value,
                                          },
                                        }))
                                        if (hasChanged) {
                                          setHasChanges(true)
                                        }
                                      }}
                                      className='w-full rounded-[8px] mb-2'
                                    />
                                  </div>
                                </div>
                              ) : (
                                <h3 className='text-sm sm:text-base font-semibold text-gray-900 break-words'>
                                  {modifiedBusinessTypes[businessType.id]
                                    ?.name || businessType.name}
                                </h3>
                              )}
                              <p className='text-xs sm:text-sm text-gray-500 mt-1'>
                                Used by {businessType.vendors_count || 0}{' '}
                                vendors
                              </p>
                            </div>
                          </div>
                          <div className='flex items-center gap-x-2 p-2 sm:p-[8px] rounded-[18px] bg-[#F5F5F5] w-fit'>
                            <span className='text-xs sm:text-sm font-medium text-gray-700'>
                              Publish
                            </span>
                            <CustomSwitch
                              checked={
                                editingBusinessTypes[businessType.id]
                                  ?.is_published ??
                                modifiedBusinessTypes[businessType.id]
                                  ?.is_published ??
                                businessType.is_published
                              }
                              currentDataChanged={currentDataChanged}
                              disabled={
                                isPublishing ||
                                isUnpublishing ||
                                !editingBusinessTypes[businessType.id]
                              }
                              disabledClassName='opacity-100'
                              onChange={() =>
                                handleTogglePublish(businessType.id)
                              }
                            />
                          </div>
                        </div>

                        <div className='flex items-center gap-2 self-end sm:self-auto flex-shrink-0'>
                          <div className='flex items-center gap-2'>
                            {editingBusinessTypes[businessType.id] ? (
                              <Button
                                variant='ghost'
                                size='icon'
                                onClick={() => handleResetEdit(businessType.id)}
                                className='text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-full'
                                aria-label='Cancel edit'
                              >
                                <X className='h-4 w-4' />
                              </Button>
                            ) : (
                              <>
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  onClick={() =>
                                    handleEditBusinessTypeModal(businessType)
                                  }
                                  className='text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-2 rounded-full'
                                  aria-label='Edit business type with image'
                                  title='Edit with image'
                                >
                                  <Edit className='h-4 w-4 text-gray-500' />
                                </Button>
                                {/* <Button
                                  variant='ghost'
                                  size='icon'
                                  onClick={() =>
                                    handleEditBusinessType(businessType)
                                  }
                                  className='text-gray-500 hover:text-gray-700 hover:bg-gray-50 p-2 rounded-full'
                                  aria-label='Quick edit name'
                                  title='Quick edit name'
                                >
                                  <Edit className='h-4 w-4' />
                                </Button> */}
                              </>
                            )}
                          </div>
                          <Button
                            variant='ghost'
                            size='icon'
                            onClick={() =>
                              handleDeleteBusinessType(businessType)
                            }
                            className='text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-full'
                            aria-label='Delete business type'
                          >
                            <DeleteIcon className='h-5 w-5 text-[#ED544E]' />
                          </Button>
                        </div>
                      </div>
                    )
                  }
                )}
              </>
            )}
          </div>

          {/* Pagination */}
          {businessTypesData?.pagination && (
            <Pagination
              currentPage={currentPage}
              totalPages={businessTypesData.pagination.last_page}
              totalItems={businessTypesData.pagination.total}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
            />
          )}

          <div className='flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-x-3 pt-4'>
            <Button
              onClick={handleSaveChanges}
              disabled={!hasChanges || isBulkUpdating}
              loading={isBulkUpdating}
              className={cn(
                'px-4 sm:px-6 py-2 rounded-lg font-medium w-full sm:w-auto',
                hasChanges && !isBulkUpdating
                  ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              )}
            >
              Save Changes
            </Button>
            <Button
              onClick={handleReset}
              variant='outline'
              className='px-4 sm:px-6 py-2 rounded-lg font-medium border-gray-300 text-gray-700 hover:bg-gray-50 w-full sm:w-auto'
            >
              Reset
            </Button>
          </div>
        </div>
      </WrapperCard>

      <AddBusinessTypeModal
        show={showAddModal}
        handleClose={() => {
          setShowAddModal(false)
        }}
        onSubmit={handleAddBusinessTypeSubmit}
        loading={isAdding}
        editingItem={null}
      />

      <AddBusinessTypeModal
        show={showEditModal}
        handleClose={() => {
          setShowEditModal(false)
          setEditingBusinessType(null)
        }}
        onSubmit={handleUpdateBusinessTypeSubmit}
        loading={isUpdating}
        editingItem={
          editingBusinessType
            ? {
                id: editingBusinessType.id,
                name: editingBusinessType.name,
                description: editingBusinessType.description,
                image_url: editingBusinessType.image_url,
                is_published: editingBusinessType.is_published,
                vendor_count: editingBusinessType.vendors_count,
              }
            : null
        }
      />

      <DeleteModal
        show={showDeleteModal}
        handleClose={handleCancelDelete}
        title='Are you sure you want to delete business type'
        itemName={businessTypeToDelete?.name || ''}
        actionBtnLoading={isDeletingAll}
        handleDelete={handleConfirmDelete}
        icon={
          <DeleteIcon className='w text-[#ED544E] bg-red-50 rounded-full p-3 size-18' />
        }
      />

      <ConfirmationModal
        show={showBulkPublishModal}
        handleClose={() => setShowBulkPublishModal(false)}
        title={`Are you sure you want to publish ${selectedBusinessTypes.length} selected business types?`}
        titleClass='text-lg SFProMedium text-gray-900 mb-2'
        message={''}
        actionBtnTitle='Publish'
        handleClick={handleConfirmBulkPublish}
        actionBtnLoading={isPublishing}
        disableActionBtn={isPublishing}
        icon={
          <div className='w-[50px] h-[50px] bg-[#295532] rounded-full flex items-center justify-center'>
            <PublishArrowUpIcon className='w-6 h-6 text-white' />
          </div>
        }
        actionType='APPROVE'
      />

      <ConfirmationModal
        show={showBulkUnpublishModal}
        handleClose={() => setShowBulkUnpublishModal(false)}
        title='Unpublish Business Types'
        message={`Are you sure you want to unpublish ${selectedBusinessTypes.length} selected business types?`}
        actionBtnTitle='Unpublish'
        handleClick={handleConfirmBulkUnpublish}
        actionBtnLoading={isUnpublishing}
        disableActionBtn={isUnpublishing}
        icon={
          <div className='w-[50px] h-[50px] bg-orange-100 rounded-full flex items-center justify-center'>
            <div className='w-6 h-6 text-orange-600'>
              {PublishArrowDownIcon}
            </div>
          </div>
        }
        actionType='APPROVE'
      />

      {/* Bulk Delete Confirmation Modal */}
      <DeleteModal
        show={showBulkDeleteModal}
        handleClose={() => setShowBulkDeleteModal(false)}
        title='Are you sure you want to delete all selected business types?'
        itemName={`${selectedBusinessTypes.length} business types`}
        actionBtnLoading={isBulkDeleting}
        disableActionBtn={isBulkDeleting}
        handleDelete={handleConfirmBulkDelete}
      />
    </div>
  )
}

export default BusinessTypes
